import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  CircularProgress,
  Radio,
  Autocomplete,
  Tooltip,
  FormHelperText,
} from "@mui/material";
import { Filter, Print, Repeat } from "@mui/icons-material";
import axios from "axios";
import { useSelector } from "react-redux";
import debounce from "lodash.debounce";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DynamicTable from "../../../../components/lab-dynamic-table";
import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";
import { generateBloodBankSlipPDF } from "../report/generateBloodBankSlipPDF";
import { useNavigate, useLocation } from "react-router-dom";
import { useBloodBankDateQuery } from "../../../blood-donor/blood-bank-date";
import DateRangeSelector from "../../../../components/DateRangeSelector";
import { SelectChangeEvent } from "@mui/material";
import InputMask from "react-input-mask";
import useDebounce from "../../../../hooks/useDebounce";

const PatientSlipForm = () => {
  const profile = useSelector((state: any) => state?.profileSlice?.profile);
  const projectProfile = useSelector(
    (state: any) => state?.projectProfileSlice?.projectProfile
  );

  const [selectedItem, setSelectedItem] = useState<any>();
  const [formData, setFormData] = useState<any>({
    patientId: "",
    bedNo: "",
    wardNo: "",
    bloodGroup: "",
    rhFactor: "",
    du: "",
    outSideRef: "",
    patient_type: "",
    remarks: "",
    bloodProducts: [],
    discount: 0,
    dicountedBy: profile?.data?.fullName
      ? profile?.data?.fullName
      : `${profile?.data?.firstName}${profile?.data?.lastName}`,
    deliveryDate: dayjs(),
    netAmount: 0,
  });
  const [allData, setAllData] = useState<any[]>([]);
  const [userInfo, setUserInfo] = useState([]);
  const [recentPatients, setRecentPatients] = useState<any[]>([]);
  const [selectedBloodProducts, setSelectedBloodProducts] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openAddPatientModal, setOpenAddPatientModal] = useState(false);
  const [newPatientData, setNewPatientData] = useState({
    mrn: "",
    patientName: "",
    cnic: "",
    mobileNo: "",
    dob: "",
    gender: "",
    city: "",
    guardiansName: "",
    helthId: "",
    reference: "",
    ref_mrn: "",
  });
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(0);
  const {
    dateRange,
    setDateRange,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
  } = useBloodBankDateQuery(setPageNo);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [total, setTotal] = useState<any>();
  const [patientSlips, setPatientSlips] = useState<any[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [editData, setEditData] = useState(null);
  const [ageInput, setAgeInput] = useState({ years: "", months: "", days: "" });
  const [ageError, setAgeError] = useState({ years: "", months: "", days: "" });
  const [error, setError] = useState<{ [key: string]: string }>({
    bloodGroup: "",
    rhFactor: "",
  });
  const [slipSearchTerm, setSlipSearchTerm] = useState("");
  const debounceSerchTerm = useDebounce(slipSearchTerm, 1000);

  const navigate = useNavigate();

  // Fetch recent patients from API
  const fetchRecentPatients = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/patient-slip/getBloodBankPatients`,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.data.isSuccess) {
        setRecentPatients(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching recent patients:", error);
      toast.error("Failed to fetch recent patients");
    }
  };

  // Set "whole blood" as default selected product when allData is loaded
  useEffect(() => {
    if (
      allData?.length > 0 &&
      selectedBloodProducts?.length === 0 &&
      !editData
    ) {
      const wholeBloodProduct = allData.find((product, i) => i === 0);
      if (wholeBloodProduct) {
        setSelectedBloodProducts([
          { ...wholeBloodProduct, qty: 1, total: wholeBloodProduct.amount },
        ]);
      }
    }
  }, [allData, editData]);

  useEffect(() => {
    if (selectedItem) {
      setFormData((prev: any) => ({
        ...prev,
        patientId: Array.isArray(selectedItem)
          ? selectedItem[0]?._id || ""
          : selectedItem?._id || "",
      }));
    }
  }, [selectedItem]);

  const buildQueryParams = () => {
    const params: any = {};
    if (searchTerm) {
      params.search = searchTerm;
      params.searchBy = "mrn";
    } else {
      params.search = "";
    }
    return params;
  };

  const handleSearch = () => {
    debouncedSearch();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      debouncedSearch();
    }
  };

  const handleDateChange = (newDate: any) => {
    setFormData({
      ...formData,
      deliveryDate: newDate,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // if (!/^\d*$/.test(value)) return; // Restrict to numbers for MRN
    setSearchTerm(value);
  };

  const donorData = async () => {
    try {
      const queryParams = buildQueryParams();
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/patient-slip/searchPatient`,
        {
          params: queryParams,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setLoading(false);
      setSelectedItem(response?.data?.data);
      if (response?.data.isSuccess === false) {
        toast.warning(`MRN: ${searchTerm}.${response?.data?.message}`);
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
      toast.error("Error searching patient");
    }
  };

  const debouncedSearch = debounce(donorData, 500);

  const handlePatientSelect = (event: any, value: any) => {
    if (value) {
      setSelectedItem(value);
      setSearchTerm(value.mrn.toString());
    } else {
      setSelectedItem(null);
      setSearchTerm("");
    }
    setPageNo(0);
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    const parsedInt = Number(value);
    if (value === "") {
      setFormData((prevData: any) => {
        return {
          ...prevData,
          [name]: "",
        };
      });
      return;
    }
    if (parsedInt < 0) {
      return;
    }
    if (name === "discount") {
      setFormData((prevData: any) => ({
        ...prevData,
        [name]: parsedInt,
      }));
    } else {
      setFormData((prevData: any) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleAddPatientModalOpen = () => {
    setOpenAddPatientModal(true);
  };

  const handleAddPatientModalClose = () => {
    setOpenAddPatientModal(false);
    setNewPatientData({
      mrn: "",
      patientName: "",
      cnic: "",
      mobileNo: "",
      dob: "",
      gender: "",
      city: "",
      guardiansName: "",
      helthId: "",
      reference: "",
      ref_mrn: "",
    });
    setAgeInput({ years: "", months: "", days: "" });
    setAgeError({ years: "", months: "", days: "" });
  };

  const handleNewPatientChange = (
    e:
      | React.ChangeEvent<{ name?: string; value: unknown }>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    if (name) {
      setNewPatientData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleAgeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (!/^\d*$/.test(value)) return; // Allow only digits

    setAgeInput((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear DOB from DatePicker when age inputs are used
    if (value) {
      setNewPatientData((prev) => ({
        ...prev,
        dob: "",
      }));
    }

    // Validate inputs
    const numValue = Number(value);
    let error = "";
    if (name === "years" && (numValue < 0 || numValue > 150)) {
      error = "0-150";
    } else if (name === "months" && (numValue < 0 || numValue > 11)) {
      error = "0-11";
    } else if (name === "days" && (numValue < 0 || numValue > 31)) {
      error = "0-31";
    }

    setAgeError((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  useEffect(() => {
    const { years, months, days } = ageInput;
    const hasInput = years || months || days;
    const hasError = Object.values(ageError).some((err) => err);

    if (hasInput && !hasError) {
      const now = dayjs();
      const calculatedDob = now
        .subtract(Number(years) || 0, "year")
        .subtract(Number(months) || 0, "month")
        .subtract(Number(days) || 0, "day");

      if (
        calculatedDob.isValid() &&
        calculatedDob.isBefore(now) &&
        calculatedDob.isAfter(dayjs("1900-01-01"))
      ) {
        const formattedDob = calculatedDob.format("YYYY-MM-DD");
        setNewPatientData((prev) => ({
          ...prev,
          dob: formattedDob,
        }));
      } else {
        setNewPatientData((prev) => ({
          ...prev,
          dob: "",
        }));
      }
    }
  }, [ageInput, ageError]);

  const handleNewPatientSubmit = async () => {
    try {
      setLoading(true);
      if (!newPatientData.patientName) {
        toast.error("Patient Name is required");
        setLoading(false);
        return;
      }
      if (!newPatientData.guardiansName) {
        toast.error("Guardian Name is required");
        setLoading(false);
        return;
      }
      if (!newPatientData.mobileNo) {
        toast.error("Phone Number is required");
        setLoading(false);
        return;
      }
      // Phone number must start with '03' and be 11 digits
      if (!/^03\d{9}$/.test(newPatientData.mobileNo)) {
        toast.error("Phone Number must start with '03' and be 11 digits.");
        setLoading(false);
        return;
      }
      if (!["Male", "Female", "Other"].includes(newPatientData.gender)) {
        toast.error("Gender must be Male, Female, or Other");
        setLoading(false);
        return;
      }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(newPatientData.dob)) {
        toast.error("Date of Birth must be in YYYY-MM-DD format");
        setLoading(false);
        return;
      }
      if (
        newPatientData.mobileNo &&
        !/^\d{1,11}$/.test(newPatientData.mobileNo)
      ) {
        toast.error("Mobile No must be 11 digits.");
        setLoading(false);
        return;
      }

      const requestBody = {
        exta: {},
        mrn: parseInt(newPatientData.mrn) || 0,
        patientName: newPatientData.patientName,
        guardiansName: newPatientData.guardiansName || "",
        gender: newPatientData.gender,
        dob: newPatientData.dob,
        phonNumber: newPatientData.mobileNo || "",
        cnic: newPatientData.cnic || "",
        helthId: newPatientData.helthId || "",
        city: newPatientData.city || "",
        reference: newPatientData.reference || "",
        ref_mrn: newPatientData.ref_mrn || "",
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BLOOD_BANK}/patient-slip/registerNewPatient`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 201) {
        const newPatient = response.data.data;
        setSelectedItem(newPatient);
        setSearchTerm(newPatient.mrn.toString());
        toast.success("Patient created successfully!");
        handleAddPatientModalClose();
        fetchRecentPatients(); // Refresh recent patients
      } else {
        toast.error(response.data.message || "Failed to create patient");
      }
    } catch (error: any) {
      console.error("Error creating patient:", error);
      toast.error(
        error.response?.data?.message?.join(", ") || "Error creating patient"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/blood-product/findAllBloodProducts`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setAllData(response.data?.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const calculateAge = (dob: string): string => {
    if (!dob) return "N/A";

    const dobDate = new Date(dob);
    const today = new Date();

    if (isNaN(dobDate.getTime())) return "N/A"; // Invalid date check

    let years = today.getFullYear() - dobDate.getFullYear();
    let months = today.getMonth() - dobDate.getMonth();
    let days = today.getDate() - dobDate.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    return `${years}Y-${months}M-${days}D`;
  };

  useEffect(() => {
    fetchData();
    fetchRecentPatients();
  }, []);

  const calculateTotalAmount = () => {
    return (
      selectedBloodProducts?.reduce((sum, item) => {
        const total = item?.total || 0;
        return sum + total;
      }, 0) || 0
    );
  };

  const calculateDiscountedAmount = (totalAmount: number, discount: number) => {
    return totalAmount - (discount || 0);
  };

  const handleCheckboxChange = (product: any, checked: boolean) => {
    if (checked) {
      setSelectedBloodProducts((prev) => [
        ...prev,
        { ...product, qty: 1, total: product.amount },
      ]);
    } else {
      setSelectedBloodProducts((prev) =>
        prev.filter((item) => item._id !== product._id)
      );
    }
  };

  const handleQuantityChange = (productId: string, qty: number) => {
    setSelectedBloodProducts((prev) =>
      prev.map((item) =>
        item._id === productId
          ? { ...item, qty, total: item.amount * qty }
          : item
      )
    );
  };
   const validateForm = () => {
    const newErrors = {
      rhFactor: "",
      bloodGroup: "",
    };
  
    let isValid = true;
  
    // Validate required fields
    if (!formData.bloodGroup) {
      newErrors.bloodGroup = "Blood Group is required";
      isValid = false;
    }
    if (!formData.rhFactor) {
      newErrors.rhFactor = "Rh Factor is required";
      isValid = false;
    }
    setError(newErrors);
    return isValid;
   }
  const handleSubmit = async () => {
    // Validate form data
    if (!validateForm()) {
      setLoading(false);
      return;
    }
    setLoading(true);
    if (!formData.patientId || typeof formData.patientId !== "string") {
      toast.error("Patient ID is missing or invalid.");
      setLoading(false);
      return;
    }
    const totalAmount = calculateTotalAmount();
    const discountedAmount = calculateDiscountedAmount(
      totalAmount,
      formData?.discount
    );

    if (editData) {
      try {
        const transformedBloodProducts = selectedBloodProducts?.map(
          (product) => ({
            name: product?.name,
            amount: product?.amount,
            qty: product?.qty,
            total: product?.total,
          })
        );
        const dataToSubmit = {
          ...formData,
          bloodProducts: transformedBloodProducts,
          netAmount: discountedAmount,
        };
        const res = await axios.put(
          `${process.env.REACT_APP_BLOOD_BANK}/patient-slip/updatePatientSlipById/${editData}`,
          dataToSubmit,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        toast.success(res?.data?.message);
        getAllPatientSlips();
        setLoading(false);
        clearFields();
      } catch (error) {
        console.error("Error submitting form:", error);
        setLoading(false);
      }
    } else {
      try {
        const transformedBloodProducts = selectedBloodProducts?.map(
          (product) => ({
            name: product?.name,
            amount: product?.amount,
            qty: product?.qty,
            total: product?.total,
          })
        );
        const dataToSubmit = {
          ...formData,
          bloodProducts: transformedBloodProducts,
          netAmount: discountedAmount,
        };
        const res = await axios.post(
          `${process.env.REACT_APP_BLOOD_BANK}/patient-slip/createPatientSlip`,
          dataToSubmit,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        handleGeneratePDF(res?.data?.data);
        navigate("/blood-crossmatch");
        toast.success(res?.data?.message);
        getAllPatientSlips();
        setLoading(false);
        clearFields();
      } catch (error) {
        console.error("Error submitting form:", error);
        setLoading(false);
      }
    }
  };

  const totalAmount = calculateTotalAmount();
  const discountedAmount = calculateDiscountedAmount(
    totalAmount,
    formData?.discount
  );

  const clearFields = () => {
    setFormData({
      patientId: "",
      bedNo: "",
      wardNo: "",
      bloodGroup: "",
      rhFactor: "",
      du: "",
      outSideRef: "",
      patient_type: "",
      remarks: "",
      bloodProducts: selectedBloodProducts,
      discount: 0,
      dicountedBy: profile?.data?.firstName,
      deliveryDate: dayjs(),
      netAmount: 0,
    });
    setSearchTerm("");
    setSelectedItem(null);
    setEditData(null);

    const wholeBloodProduct = allData.find((product, i) => i === 0);
    if (wholeBloodProduct) {
      const isWholeBloodSelected = selectedBloodProducts.some(
        (item) => item._id === wholeBloodProduct._id
      );
      if (!isWholeBloodSelected) {
        setSelectedBloodProducts([
          { ...wholeBloodProduct, qty: 1, total: wholeBloodProduct.amount },
        ]);
        setFormData((prevData: any) => ({
          ...prevData,
          bloodProducts: [
            { ...wholeBloodProduct, qty: 1, total: wholeBloodProduct.amount },
          ],
        }));
      }
    }
  };

  useEffect(() => {
    const totalAmount = calculateTotalAmount();
    const discountedAmount = calculateDiscountedAmount(
      totalAmount,
      formData?.discount
    );
    setFormData((prevData: any) => ({
      ...prevData,
      netAmount: discountedAmount || totalAmount,
    }));
  }, [discountedAmount, selectedBloodProducts, formData?.discount]);

  const getAllPatientSlips = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/patient-slip/findAllPatientSlips`,
        {
          params: {
            pageNo: pageNo + 1,
            count: rowsPerPage,
            search: slipSearchTerm,
            ...(fromDate ? { fromDate } : {}),
            ...(toDate ? { toDate } : {}),
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setPatientSlips(response?.data?.data);
      setTotal(response?.data?.totalCount);
    } catch (error) {
      console.log(error);
    }
  };

const handleSlipSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {

    setSlipSearchTerm(e.target.value);
  };

  useEffect(() => {
    getAllPatientSlips();
  }, [pageNo, rowsPerPage, debounceSerchTerm, fromDate, toDate]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageNo(newPage);
  };

  const handleChangeRowsPerPage = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageNo(0);
  };

  useEffect(() => {
    setFormData({
      ...formData,
      bloodProducts: selectedBloodProducts,
    });
  }, [selectedBloodProducts]);

  const handleEditClick = (id: any, rowData: any) => {
    setEditData(id);
    setFormData({
      patientId: rowData?.patientId,
      bedNo: rowData?.bedNo,
      wardNo: rowData?.wardNo,
      bloodGroup: rowData?.bloodGroup,
      rhFactor: rowData?.rhFactor,
      du: rowData?.du,
      outSideRef: rowData?.outSideRef,
      patient_type: rowData?.patient_type,
      remarks: rowData?.remarks,
      discount: rowData?.discount || 0,
      dicountedBy: `${profile?.data?.firstName}${profile?.data?.lastName}`,
      deliveryDate: dayjs(rowData?.deliveryDate),
      netAmount: rowData?.netAmount || 0,
    });

    const normalizedBloodProducts =
      rowData?.bloodProducts?.map((bp: any) => {
        const productMatch = allData?.find(
          (product: any) => product.name === bp.name
        );
        return {
          _id: productMatch?._id || bp._id || "",
          name: bp.name,
          amount: productMatch?.amount || bp.amount || 0,
          qty: bp.qty || 1,
          total:
            bp.total || (productMatch?.amount || bp.amount) * (bp.qty || 1),
        };
      }) || [];

    setSelectedBloodProducts(normalizedBloodProducts);
    setSelectedItem(rowData?.patientId);
  };

  const handleGeneratePDF = (rowData: any) => {
    const totalAmount =
      rowData.bloodProducts?.reduce(
        (sum: number, bp: any) => sum + (bp.total || 0),
        0
      ) || 0;
    const discount = rowData.discount || 0;
    const netAmount = rowData.netAmount || totalAmount - discount;

    const bloodBankData = {
      hospitalName: projectProfile.hospitalName || "",
      hospitalLocation: projectProfile.city,
      hospitalDetails: rowData.hospitalDetails,
      slipNo: rowData.slipNo || "N/A",
      mrNo: rowData.patientDetails?.mrn || "N/A",
      refMrn: rowData.patientDetails?.ref_mrn || "--",
      patientName: rowData.patientDetails?.patientName || "Unknown",
      gender: rowData.patientDetails?.gender || "N/A",
      age: calculateAge(rowData.patientDetails?.dob), // Compute age from DOB
      bloodGroup: rowData.bloodGroup || "N/A",
      rhFactor: rowData.rhFactor || "N/A",
      hospital: projectProfile.hospitalName || "N/A",
      phone: rowData.patientDetails?.phonNumber || "N/A",
      date: dayjs().format("DD-MMM-YYYY"),
      delivery: dayjs(rowData.deliveryDate).format("DD-MMM-YYYY"),
      patientType: rowData.patient_type || "N/A",
      du: rowData.du || "N/A",
      outSideRef: rowData.outSideRef || "N/A",
      requirements:
        rowData.bloodProducts?.map((bp: any) => ({
          item: bp.name,
          rate: bp.amount || 0,
          subsidy: 0,
          quantity: bp.qty || 1,
          amount: bp.total || 0,
        })) || [],
      financials: {
        subTotal: totalAmount,
        subsidiary: 0,
        grossAmount: totalAmount,
        discount: discount,
        paid: netAmount,
        due: 0,
      },
      remarks: rowData.remarks || "No remarks available",
      notes: [
        "This is a computer-generated slip, requires no signature or stamp.",
        "Blood Product, Cross Match & Screening Charges are non-refundable.",
      ],
      discountedBy: rowData.dicountedBy || "N/A",
      pageHeaderData: rowData.pageHeaderData || undefined,
    };

    if (!bloodBankData.financials.subTotal || !bloodBankData.financials.paid) {
      console.error("Invalid financial data:", bloodBankData.financials);
      toast.error("Cannot generate PDF: Invalid financial data");
      return;
    }

    generateBloodBankSlipPDF(bloodBankData);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteClick = (id: any) => {
    setIsDeleteModalOpen(true);
    setSelectedItemId(id);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Authorization token is missing");
        return;
      }
      const response = await axios.delete(
        `${process.env.REACT_APP_BLOOD_BANK}/patient-slip/deletePatientSlipById/${selectedItemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchData();
      toast.success(response.data.message);
      clearFields();
      setEditData(null);
      getAllPatientSlips();
    } catch (error: any) {
      toast.error(error.message);
    }
    setIsDeleteModalOpen(false);
  };

  const handleBox = (data: any) => {
    setSelectedItem(data);
    setSearchTerm(data.mrn.toString());
    handleCloseModal();
  };

  const isValidPhoneNumber = (phone: string) =>
    phone === "" || /^\+923\d{8}$/.test(phone);

  const isValidDate = (date: string) =>
    date === "" || /^\d{4}-\d{2}-\d{2}$/.test(date);

  return (
    <Box >
      <Box sx={{ background: "#fff", borderRadius: 1, mb: 0.5, p: 0.5 }}>
        <Grid container>
          <Grid item md={5} sm={4} xs={12}>
            <h1
              style={{ fontSize: "14px", fontWeight: "bold" }}
            >
              Patient Slip Form
            </h1>
          </Grid>
        </Grid>
      </Box>

      <div style={{ padding: "5px", borderRadius: "8px", height: "auto", background: "#fff" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box>
              <Box
                sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}
              >
                <TextField
                  size="small"
                  className="search-field"
                  placeholder=" By MRN & Ref MRN..."
                  value={searchTerm}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  sx={{
                    width: 200,
                    backgroundColor: "#F5F5FB",
                    borderRadius: "8px",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "action.active" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear search"
                          onClick={() => {
                            setSearchTerm("");
                            setSelectedItem(null);
                            setPageNo(0);
                          }}
                          edge="end"
                          size="small"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />

                <Autocomplete
                  options={recentPatients}
                  getOptionLabel={(option) =>
                    `${option.mrn} - ${option.patientName}`
                  }
                  value={
                    recentPatients.find(
                      (patient) => patient.mrn.toString() === searchTerm
                    ) || null
                  }
                  onChange={handlePatientSelect}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Recent Patients"
                      size="small"
                      className="search-field"
                      sx={{
                        width: 200,
                        backgroundColor: "#F5F5FB",
                        borderRadius: "8px",
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {searchTerm ? (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="clear selection"
                                  onClick={() =>
                                    handlePatientSelect(null, null)
                                  }
                                  edge="end"
                                  size="small"
                                >
                                  {/* <CloseIcon fontSize="small" /> */}
                                </IconButton>
                              </InputAdornment>
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiAutocomplete-inputRoot": {
                      height: "38px",
                      padding: "0 14px",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "12px",
                    },
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option._id === value?._id
                  }
                />

                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleAddPatientModalOpen}
                  sx={{ height: "38px", fontSize: "12px" }}
                >
                  Add New Patient
                </Button>
              </Box>

              <Box
                sx={{
                  boxShadow: "0px 5px 10px rgba(0, 0, 0, 0.1)",
                  p: 2,
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <Typography sx={{ fontSize: "12px" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(6, 1fr)",
                      gap: "10px",
                      width: "100%",
                    }}
                  >
                    <div style={{ display: "grid", gap: "2px" }}>
                      <span style={{ fontWeight: "bold" }}>MRN:</span>
                      <span>
                        {selectedItem?.mrn ||
                          (selectedItem
                            ? selectedItem[0]?.mrn
                            : "- - - - - -")}
                      </span>
                    </div>
                    <div style={{ display: "grid", gap: "2px" }}>
                      <span style={{ fontWeight: "bold" }}>Ref MRN:</span>
                      <span>
                        {selectedItem?.ref_mrn ||
                          (selectedItem
                            ? selectedItem[0]?.ref_mrn
                            : "- - - - - -")}
                      </span>
                    </div>
                    <div style={{ display: "grid", gap: "2px" }}>
                      <span style={{ fontWeight: "bold" }}>Name:</span>
                      <span>
                        {selectedItem?.patientName ||
                          (selectedItem
                            ? selectedItem[0]?.patientName
                            : "- - - - - -")}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "2px",
                        flexDirection: "column",
                      }}
                    >
                      <span style={{ fontWeight: "bold" }}>DOB:</span>
                      <span>
                        {selectedItem?.dob ||
                          (selectedItem ? selectedItem[0]?.dob : "- - - - - -")}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "2px",
                        flexDirection: "column",
                      }}
                    >
                      <span style={{ fontWeight: "bold" }}>Gender:</span>
                      <span>
                        {selectedItem?.gender ||
                          (selectedItem
                            ? selectedItem[0]?.gender
                            : "- - - - - -")}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "2px",
                        flexDirection: "column",
                      }}
                    >
                      <span style={{ fontWeight: "bold" }}>City:</span>
                      <span>
                        {selectedItem?.city ||
                          (selectedItem
                            ? selectedItem[0]?.city
                            : "- - - - - -")}
                      </span>
                    </div>
                  </div>
                </Typography>
              </Box>

              <Grid container spacing={1}>
                <Grid item xs={3} md={3}>
                  <TextField
                    label="Bed No"
                    variant="outlined"
                    fullWidth
                    name="bedNo"
                    value={formData?.bedNo}
                    onChange={handleChange}
                    size="small"
                    className="search-field"
                    InputLabelProps={{ style: { fontSize: "12px" } }}
                    sx={{
                      height: "38px",
                      "& .MuiInputBase-root": { height: "38px" },
                      "& .MuiOutlinedInput-input": { padding: "6px 8px" },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={3} md={3}>
                  <TextField
                    label="Ward No"
                    variant="outlined"
                    fullWidth
                    name="wardNo"
                    value={formData?.wardNo}
                    className="search-field"
                    onChange={handleChange}
                    size="small"
                    InputLabelProps={{ style: { fontSize: "12px" } }}
                    sx={{
                      height: "38px",
                      "& .MuiInputBase-root": { height: "38px" },
                      "& .MuiOutlinedInput-input": { padding: "6px 8px" },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={3} md={3}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel sx={{ fontSize: "11px" }}>
                      Blood Group
                    </InputLabel>
                    <Select
                      label="Blood Group"
                      name="bloodGroup"
                      value={formData?.bloodGroup}
                      onChange={handleChange}
                      // required
                      size="small"
                      sx={{
                        borderRadius: "12px",
                        background: "#F5F5FB",
                        fontSize: "12px",
                        height: "38px",
                        "& fieldset": { border: "none" },
                      }}
                    >
                      <MenuItem value="A">A</MenuItem>
                      <MenuItem value="B">B</MenuItem>
                      <MenuItem value="AB">AB</MenuItem>
                      <MenuItem value="O">O</MenuItem>
                    </Select>
                  </FormControl>
                  {error.bloodGroup && (
                    <FormHelperText error>{error.bloodGroup}</FormHelperText>
                  )}
                </Grid>
                <Grid item xs={3} md={3}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel sx={{ fontSize: "12px" }}>Rh Factor</InputLabel>
                    <Select
                      label="Rh Factor"
                      name="rhFactor"
                      value={formData?.rhFactor}
                      onChange={handleChange}
                      size="small"
                      sx={{
                        borderRadius: "12px",
                        background: "#F5F5FB",
                        fontSize: "12px",
                        height: "38px",
                        "& fieldset": { border: "none" },
                      }}
                    >
                      <MenuItem value="+">+</MenuItem>
                      <MenuItem value="-">-</MenuItem>
                    </Select>
                  </FormControl>
                  {error.rhFactor && (
                    <FormHelperText error>{error?.rhFactor}</FormHelperText>
                  )}
                </Grid>

                <Grid item xs={3} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: "12px" }}>DU</InputLabel>
                    <Select
                      label="DU"
                      name="du"
                      value={formData?.du}
                      onChange={handleChange}
                      size="small"
                      sx={{
                        borderRadius: "12px",
                        background: "#F5F5FB",
                        fontSize: "12px",
                        height: "38px",
                        "& fieldset": { border: "none" },
                      }}
                    >
                      <MenuItem value="Du+">Du+</MenuItem>
                      <MenuItem value="Du-">Du-</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={3} md={3}>
                  <TextField
                    label="Outside Ref"
                    variant="outlined"
                    fullWidth
                    name="outSideRef"
                    size="small"
                    className="search-field"
                    value={formData?.outSideRef}
                    onChange={handleChange}
                    InputLabelProps={{ style: { fontSize: "12px" } }}
                    sx={{
                      height: "38px",
                      "& .MuiInputBase-root": { height: "38px" },
                      "& .MuiOutlinedInput-input": { padding: "6px 8px" },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={3} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: "12px" }}>
                      Patient Type
                    </InputLabel>
                    <Select
                      label="Patient Type"
                      name="patient_type"
                      value={formData?.patient_type}
                      onChange={handleChange}
                      size="small"
                      sx={{
                        borderRadius: "12px",
                        background: "#F5F5FB",
                        fontSize: "12px",
                        height: "38px",
                        "& fieldset": { border: "none" },
                      }}
                    >
                      <MenuItem value="IN_HOUSE">IN_HOUSE</MenuItem>
                      <MenuItem value="OUTSIDE">OUTSIDE</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={3} md={3}>
                  <TextField
                    label="Remarks"
                    variant="outlined"
                    fullWidth
                    name="remarks"
                    className="search-field"
                    value={formData?.remarks}
                    onChange={handleChange}
                    size="small"
                    InputLabelProps={{ style: { fontSize: "12px" } }}
                    sx={{
                      height: "38px",
                      "& .MuiInputBase-root": { height: "38px" },
                      "& .MuiOutlinedInput-input": { padding: "6px 8px" },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <h3
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    fontFamily: "Arial, sans-serif",
                    color: "#333",
                    margin: 0,
                    padding: 0,
                    textAlign: "left",
                    letterSpacing: "0.5px",
                  }}
                >
                  Blood Products
                </h3>
                <Box
                  sx={{
                    maxHeight: { md: "25vh", sm: "auto", xs: "40vh" },
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <TableContainer
                    component={Paper}
                    sx={{
                      maxHeight: { md: "12vh", sm: "auto", xs: "auto" },
                      width: "100%",
                      "&::-webkit-scrollbar": {
                        width: "8px",
                        height: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f1f1f1",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#077DD3",
                        borderRadius: "4px",
                        "&:hover": {
                          backgroundColor: "#0669b3",
                        },
                      },
                      scrollbarWidth: "thin",
                      scrollbarColor: "#077DD3 #f1f1f1",
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ background: "#077DD3" }}>
                          <TableCell
                            sx={{
                              fontSize: "8px",
                              fontWeight: "bold",
                              p: 0.2,
                              color: "#fff",
                              position: "sticky",
                              top: 0,
                              zIndex: 1,
                              background: "#077DD3",
                            }}
                          >
                            Checkbox
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "8px",
                              fontWeight: "bold",
                              p: 0.2,
                              color: "#fff",
                              position: "sticky",
                              top: 0,
                              zIndex: 1,
                              background: "#077DD3",
                            }}
                          >
                            Blood Product
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "8px",
                              fontWeight: "bold",
                              p: 0.2,
                              color: "#fff",
                              position: "sticky",
                              top: 0,
                              zIndex: 1,
                              background: "#077DD3",
                            }}
                          >
                            Unit Amount
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "8px",
                              fontWeight: "bold",
                              p: 0.2,
                              color: "#fff",
                              position: "sticky",
                              top: 0,
                              zIndex: 1,
                              background: "#077DD3",
                            }}
                          >
                            Quantity
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "8px",
                              fontWeight: "bold",
                              p: 0.2,
                              color: "#fff",
                              position: "sticky",
                              top: 0,
                              zIndex: 1,
                              background: "#077DD3",
                            }}
                          >
                            Total
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allData?.map((product) => (
                          <TableRow key={product._id}>
                            <TableCell sx={{ p: 0.2, fontSize: "10px" }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={selectedBloodProducts?.some(
                                      (item) => item?._id === product?._id
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        product,
                                        e.target.checked
                                      )
                                    }
                                    sx={{ p: "0.2px", ml: "20px" }}
                                  />
                                }
                                label=""
                              />
                            </TableCell>
                            <TableCell sx={{ p: 0.2, fontSize: "10px" }}>
                              {product?.name}
                            </TableCell>
                            <TableCell sx={{ p: 0.2, fontSize: "10px" }}>
                              {product?.amount}
                            </TableCell>
                            <TableCell sx={{ p: 0.2, fontSize: "10px" }}>
                              {selectedBloodProducts?.some(
                                (item) => item._id === product._id
                              ) && (
                                  <TextField
                                    placeholder="Quantity"
                                    variant="outlined"
                                    className="search-field"
                                    type="number"
                                    value={
                                      selectedBloodProducts?.find(
                                        (item) => item?._id === product?._id
                                      )?.qty || 1
                                    }
                                    onChange={(e) =>
                                      handleQuantityChange(
                                        product._id,
                                        Number(e.target.value)
                                      )
                                    }
                                    sx={{
                                      width: "80px",
                                      height: "20px",
                                      "& .MuiInputBase-root": { height: "20px" },
                                      "& .MuiOutlinedInput-input": {
                                        padding: "6px 8px",
                                      },
                                    }}
                                    size="small"
                                    fullWidth
                                  />
                                )}
                            </TableCell>
                            <TableCell sx={{ p: 0.2 }}>
                              {selectedBloodProducts?.some(
                                (item) => item?._id === product._id
                              ) && (
                                  <TextField
                                    placeholder="Total"
                                    variant="outlined"
                                    className="search-field"
                                    value={
                                      selectedBloodProducts?.find(
                                        (item) => item._id === product._id
                                      )?.total || 0
                                    }
                                    disabled
                                    sx={{
                                      width: "80px",
                                      height: "20px",
                                      "& .MuiInputBase-root": { height: "20px" },
                                      "& .MuiOutlinedInput-input": {
                                        padding: "6px 8px",
                                      },
                                    }}
                                    size="small"
                                    fullWidth
                                  />
                                )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end">
                  <Typography variant="h6" sx={{ fontSize: "12px" }}>
                    Total Amount: {totalAmount.toFixed(2)} RS
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} md={3}>
                <TextField
                  label="Total Amount"
                  variant="outlined"
                  fullWidth
                  name="totalAmount"
                  value={totalAmount}
                  className="search-field"
                  disabled
                  size="small"
                  type="number"
                  InputLabelProps={{ style: { fontSize: "10px" } }}
                  InputProps={{ style: { fontSize: "10px" } }}
                  sx={{
                    height: "38px",
                    "& .MuiInputBase-root": { height: "38px" },
                    "& .MuiOutlinedInput-input": { padding: "6px 8px" },
                  }}
                />
              </Grid>

              <Grid item xs={6} md={3}>
                <TextField
                  label="Discount"
                  variant="outlined"
                  fullWidth
                  name="discount"
                  value={formData?.discount}
                  onChange={handleChange}
                  size="small"
                  className="search-field"
                  type="number"
                  InputLabelProps={{ style: { fontSize: "10px" } }}
                  InputProps={{ style: { fontSize: "10px" } }}
                  sx={{
                    height: "38px",
                    "& .MuiInputBase-root": { height: "38px" },
                    "& .MuiOutlinedInput-input": { padding: "6px 8px" },
                  }}
                />
              </Grid>

              <Grid item xs={6} md={3}>
                <TextField
                  label="Gross Amount"
                  variant="outlined"
                  fullWidth
                  name="grossAmount"
                  value={discountedAmount}
                  disabled
                  size="small"
                  className="search-field"
                  type="number"
                  InputLabelProps={{ style: { fontSize: "10px" } }}
                  InputProps={{ style: { fontSize: "10px" } }}
                  sx={{
                    height: "38px",
                    "& .MuiInputBase-root": { height: "38px" },
                    "& .MuiOutlinedInput-input": { padding: "4px 4px" },
                  }}
                />
              </Grid>

              <Grid item xs={6} md={3}>
                <TextField
                  label="Discount By"
                  variant="outlined"
                  fullWidth
                  name="dicountedBy"
                  value={formData?.dicountedBy}
                  disabled
                  size="small"
                  className="search-field"
                  InputLabelProps={{ style: { fontSize: "10px" } }}
                  InputProps={{ style: { fontSize: "10px" } }}
                  sx={{
                    height: "38px",
                    "& .MuiInputBase-root": { height: "38px" },
                    "& .MuiOutlinedInput-input": { padding: "4px 8px" },
                  }}
                />
              </Grid>

              <Grid item xs={6} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Delivery Date & Time"
                    value={formData?.deliveryDate}
                    onChange={handleDateChange}
                    format="DD.MM.YYYY hh:mm A"
                    className="search-field"
                    sx={{ marginTop: "" }}
                    slotProps={{ textField: { size: "small" } }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={6} md={6}>
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleSubmit}
                    style={{ marginTop: "6px" }}
                    // disabled={
                    //   !(
                    //     selectedItem &&
                    //     formData?.bloodGroup &&
                    //     formData?.rhFactor &&
                    //     formData?.bloodProducts?.length > 0
                    //   )
                    // }
                  >
                    {loading ? (
                      <CircularProgress sx={{ color: "#fff" }} />
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} height={"auto"}>
            <Box
              mt={2}
              sx={{
                height: "100%",
                "& .MuiTableContainer-root": {
                  maxHeight: "39vh",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f1f1",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#077DD3",
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: "#0669b3",
                    },
                  },
                  scrollbarWidth: "thin",
                  scrollbarColor: "#077DD3 #f1f1f1",
                },
                "& .MuiTableHead-root .MuiTableRow-root": {
                  "& .MuiTableCell-root": {
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "#077DD3",
                    color: "#fff",
                  },
                },
              }}
            >

          <Box mb={2} display="flex" justifyContent="flex-end" alignItems="flex-end" gap={1.5} width="100%" flexWrap="wrap">
            <DateRangeSelector
              preset="blood"
              compact
              dateRange={dateRange}
              fromDate={fromDate}
              toDate={toDate}
              onDateRangeChange={value => {
                setDateRange(value);
                setPageNo(0);
              }}
              onFromDateChange={value => {
                setFromDate(value);
                setPageNo(0);
              }}
              onToDateChange={value => {
                setToDate(value);
                setPageNo(0);
              }}
            />
            <TextField
              size="small"
              className="search-field"
              placeholder="Search BY P.Name or MRN..."
              value={slipSearchTerm}
              onChange={handleSlipSearchChange}
              sx={{
                width: 250,
                backgroundColor: "#F5F5FB",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "action.active" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>



              <DynamicTable
                headers={[
                  "SN",
                  "mrn",
                  "Ref MRN",
                  "Patient Name",
                  "Slip#",
                  "Ward#",
                  "Bed#",
                  "BG",
                  "Blood Products",
                  // "DU",
                  "Discount By",
                  "Discount",
                  "Net Amount",
                  "Delivery Date",
                  "Action",
                ]}
                data={patientSlips?.map((item: any, index: number) => [
                  pageNo * rowsPerPage + index + 1,
                  item?.patientDetails?.mrn,
                  item?.patientDetails?.ref_mrn || "--",
                  item?.patientDetails?.patientName,
                  item?.slipNo,
                  item?.wardNo,
                  item?.bedNo,
                  `${item?.bloodGroup} ${item?.rhFactor} `,
                  <Select
                    value={item?.bloodProducts?.[0]?.name || ""}
                    size="small"
                    fullWidth
                    sx={{
                      borderRadius: "12px",
                      background: "#F5F5FB",
                      fontSize: "12px",
                      height: "38px",
                      "& fieldset": { border: "none" },
                    }}
                  >
                    {item?.bloodProducts?.map((bp: any, idx: number) => (
                      <MenuItem key={idx} value={bp?.name}>
                        {bp?.name} - {bp?.total}
                      </MenuItem>
                    )) || <MenuItem value="">No Products</MenuItem>}
                  </Select>,
                  // item?.du,
                  item?.dicountedBy,
                  item?.discount,
                  item?.netAmount,
                  dayjs(item?.deliveryDate).format("DD.MM.YYYY hh:mm A"),
                ])}
                loading={false}
                page={pageNo}
                rowsPerPage={rowsPerPage}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                totalRows={total}
                actionButtons={[
                  {
                    icon: (
                      <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />
                    ),
                    onClick: (rowIndex: any) =>
                      handleEditClick(
                        patientSlips[rowIndex]?._id,
                        patientSlips[rowIndex]
                      ),
                  },
                  {
                    icon: <Print sx={{ color: "green", cursor: "pointer" }} />,
                    onClick: (rowIndex: any) =>
                      handleGeneratePDF(patientSlips[rowIndex]),
                  },
                  {
                    icon: (
                      <DeleteIcon
                        sx={{ color: "#FF5454", cursor: "pointer" }}
                      />
                    ),
                    onClick: (rowIndex: any) =>
                      handleDeleteClick(patientSlips[rowIndex]?._id),
                  },
                  {
                    icon: (
                      <Tooltip title="Re issue" placement="top">
                        <Repeat sx={{ color: "#FF5454", cursor: "pointer" }} />
                      </Tooltip>
                    ),
                    onClick: (rowIndex: any) =>
                      setSelectedItem(patientSlips[rowIndex]?.patientDetails),
                  }

                ]}
              />
            </Box>
          </Grid>
        </Grid>

        <DeleteConfirmationModal
          open={isDeleteModalOpen}
          handleClose={handleDeleteModalClose}
          handleConfirm={handleDeleteConfirm}
        />

        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
        >
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            flexDirection={"column"}
          >
            <Typography sx={{ fontWeight: "bold" }}>Searched list</Typography>
          </Box>
          <Box
            sx={{
              maxHeight: "calc(200px)",
              overflow: "hidden",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "6px",
              },
            }}
          >
            <TableContainer
              component={Paper}
              style={{ border: "none", overflow: "hidden" }}
            >
              <Table
                sx={{
                  textAlign: "center",
                  overflowY: "hidden",
                  height: "100%",
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>MRN</TableCell>
                    <TableCell>Ref MRN</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Guardian Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userInfo?.map((row: any, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                          cursor: "pointer",
                        },
                      }}
                      onClick={() => handleBox(row)}
                    >
                      <TableCell>{row.mrn}</TableCell>
                      <TableCell>{row.ref_mrn || "--"}</TableCell>
                      <TableCell>{row.patientName}</TableCell>
                      <TableCell>{row.guardiansName}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Dialog>

        <Dialog
          open={openAddPatientModal}
          onClose={handleAddPatientModalClose}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: "24px", fontWeight: "bold" }}>
            PATIENT REGISTRATIO
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={3}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#000",
                    fontWeight: "700",
                    mb: 0.5,
                  }}
                >
                  PATIENT NAME <span style={{ color: "red" }}>*</span> :
                </Typography>
                <TextField
                  placeholder="Patient Name"
                  className="custom-input"
                  name="patientName"
                  value={newPatientData.patientName}
                  onChange={handleNewPatientChange}
                  fullWidth
                  required
                  size="small"
                  FormHelperTextProps={{
                    sx: { fontSize: "10px" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#000",
                    fontWeight: "700",
                    mb: 0.5,
                  }}
                >
                  GUARDIAN NAME <span style={{ color: "red" }}>*</span> :
                </Typography>
                <TextField
                  placeholder="Guardian Name"
                  name="guardiansName"
                  className="custom-input"
                  value={newPatientData.guardiansName}
                  onChange={handleNewPatientChange}
                  fullWidth
                  required
                  size="small"
                  FormHelperTextProps={{
                    sx: { fontSize: "10px" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#000",
                    fontWeight: "700",
                    mb: 0.5,
                  }}
                >
                  PHONE NUMBER <span style={{ color: "red" }}>*</span> :
                </Typography>
                <InputMask
                  mask="03999999999"
                  value={newPatientData.mobileNo}
                  onChange={(e: any) =>
                    setNewPatientData((prev) => ({
                      ...prev,
                      mobileNo: e.target.value,
                    }))
                  }
                >
                  {(inputProps: any) => (
                    <TextField
                      {...inputProps}
                      placeholder="03XXXXXXXXX"
                      name="mobileNo"
                      className="custom-input"
                      fullWidth
                      required
                      size="small"
                      FormHelperTextProps={{
                        sx: { fontSize: "10px" },
                      }}
                    />
                  )}
                </InputMask>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#000",
                    fontWeight: "700",
                    mb: 0.5,
                  }}
                >
                  CNIC <span style={{ color: "gray", fontSize: "8px" }}>(optional)</span> :
                </Typography>
                <TextField
                  placeholder="CNIC"
                  className="custom-input"
                  name="cnic"
                  value={newPatientData.cnic}
                  onChange={handleNewPatientChange}
                  fullWidth
                  size="small"
                  FormHelperTextProps={{
                    sx: { fontSize: "10px" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#000",
                    fontWeight: "700",
                    mb: 0.5,
                  }}
                >
                  GENDER <span style={{ color: "red" }}>*</span> :
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                  <FormControlLabel
                    control={
                      <Radio
                        checked={newPatientData.gender === "Male"}
                        onChange={handleNewPatientChange}
                        value="Male"
                        name="gender"
                        sx={{ "& .MuiSvgIcon-root": { fontSize: 13 } }}
                      />
                    }
                    label="Male"
                    sx={{ fontSize: "11px", mr: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={newPatientData.gender === "Female"}
                        onChange={handleNewPatientChange}
                        value="Female"
                        name="gender"
                        sx={{ "& .MuiSvgIcon-root": { fontSize: 13 } }}
                      />
                    }
                    label="Female"
                    sx={{ fontSize: "11px", mr: 0 }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#000",
                    fontWeight: "700",
                    mb: 0.5,
                  }}
                >
                  DATE OF BIRTH <span style={{ color: "red" }}>*</span> :
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={
                      newPatientData.dob ? dayjs(newPatientData.dob) : null
                    }
                    onChange={(date) => {
                      if (date && dayjs(date).isValid()) {
                        const formattedDate = dayjs(date).format(
                          "YYYY-MM-DD"
                        );
                        setNewPatientData((prev) => ({
                          ...prev,
                          dob: formattedDate,
                        }));
                        setAgeInput({ years: "", months: "", days: "" });
                        setAgeError({ years: "", months: "", days: "" });
                      } else {
                        setNewPatientData((prev) => ({
                          ...prev,
                          dob: "",
                        }));
                      }
                    }}
                    maxDate={dayjs()}
                    minDate={dayjs("1900-01-01")}
                    format="DD/MM/YYYY"
                    openTo="year"
                    views={["year", "month", "day"]}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        className: "custom-input",
                        FormHelperTextProps: {
                          sx: { fontSize: "10px" },
                        },
                      },
                    }}
                    sx={{
                      width: "100%",
                      "& fieldset": {
                        border: "none",
                      },
                      "& .MuiInputBase-root": {
                        minHeight: "32px",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#000",
                    fontWeight: "700",
                    mb: 0.5,
                  }}
                >
                  AGE (YY-MM-DD) <span style={{ color: "red" }}>*</span> :
                </Typography>
                <Box sx={{ display: "flex", gap: 0.3 }}>
                  <TextField
                    label="YY"
                    name="years"
                    value={ageInput.years}
                    onChange={handleAgeInputChange}
                    size="small"
                    type="number"
                    error={!!ageError.years}
                    helperText={ageError.years}
                    className="custom-input"
                    sx={{ width: "50px" }}
                    FormHelperTextProps={{
                      sx: { fontSize: "10px" },
                    }}
                  />
                  <TextField
                    label="MM"
                    name="months"
                    value={ageInput.months}
                    onChange={handleAgeInputChange}
                    size="small"
                    type="number"
                    error={!!ageError.months}
                    helperText={ageError.months}
                    className="custom-input"
                    sx={{ width: "50px" }}
                    FormHelperTextProps={{
                      sx: { fontSize: "10px" },
                    }}
                  />
                  <TextField
                    label="DD"
                    name="days"
                    value={ageInput.days}
                    onChange={handleAgeInputChange}
                    size="small"
                    type="number"
                    error={!!ageError.days}
                    helperText={ageError.days}
                    className="custom-input"
                    sx={{ width: "50px" }}
                    FormHelperTextProps={{
                      sx: { fontSize: "10px" },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#000",
                    fontWeight: "700",
                    mb: 0.5,
                  }}
                >
                  HEALTH ID <span style={{ color: "gray", fontSize: "8px" }}>(optional)</span> :
                </Typography>
                <TextField
                  placeholder="Health ID"
                  name="helthId"
                  value={newPatientData.helthId}
                  onChange={handleNewPatientChange}
                  className="custom-input"
                  fullWidth
                  size="small"
                  FormHelperTextProps={{
                    sx: { fontSize: "10px" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#000",
                    fontWeight: "700",
                    mb: 0.5,
                  }}
                >
                  CITY <span style={{ color: "gray", fontSize: "8px" }}>(optional)</span> :
                </Typography>
                <TextField
                  placeholder="City"
                  name="city"
                  className="custom-input"
                  value={newPatientData.city}
                  onChange={handleNewPatientChange}
                  fullWidth
                  size="small"
                  FormHelperTextProps={{
                    sx: { fontSize: "10px" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#000",
                    fontWeight: "700",
                    mb: 0.5,
                  }}
                >
                  REFERENCE <span style={{ color: "gray", fontSize: "8px" }}>(optional)</span> :
                </Typography>
                <TextField
                  placeholder="Reference"
                  name="reference"
                  className="custom-input"
                  value={newPatientData.reference}
                  onChange={handleNewPatientChange}
                  fullWidth
                  size="small"
                  FormHelperTextProps={{
                    sx: { fontSize: "10px" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#000",
                    fontWeight: "700",
                    mb: 0.5,
                  }}
                >
                  REF MRN <span style={{ color: "gray", fontSize: "8px" }}>(optional)</span> :
                </Typography>
                <TextField
                  placeholder="Ref MRN"
                  name="ref_mrn"
                  className="custom-input"
                  value={newPatientData.ref_mrn}
                  onChange={handleNewPatientChange}
                  fullWidth
                  size="small"
                  FormHelperTextProps={{
                    sx: { fontSize: "10px" },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleAddPatientModalClose}
              size="small"
              sx={{
                borderRadius: "10px",
                textTransform: "uppercase",
                color: "#077DD3",
                border: "1px solid #077DD3",
                padding: "8px 16px",
              }}
            >
              Back
            </Button>
            <Button
              onClick={handleNewPatientSubmit}
              variant="contained"
              color="primary"
              size="small"
              sx={{
                borderRadius: "10px",
                textTransform: "uppercase",
                padding: "8px 16px",
              }}
            >
              {loading ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Box>
  );
};

export default PatientSlipForm;
